import { computed, ref } from "vue";
import { parseUploadError, uploadDocumentFile } from "@/services/uploadService";
/**
 * Stages the files of one upload. Each file goes to S3 as soon as it is added, so its link is ready
 * before the user picks a destination. A failed file keeps its row with the error. Files beyond `maxFiles` are
 * not added.
 */
export function useDocumentUploads(maxFiles) {
    const documents = ref([]);
    const notice = ref(null);
    const links = computed(() => documents.value.flatMap((doc) => (doc.link ? [doc.link] : [])));
    const uploading = computed(() => documents.value.some((doc) => doc.uploading));
    const uploadOne = async (doc) => {
        try {
            doc.link = await uploadDocumentFile(doc.file);
        }
        catch (error) {
            doc.error = parseUploadError(error);
        }
        finally {
            doc.uploading = false;
        }
    };
    const capMessage = (selected, room) => room > 0
        ? `You selected ${selected} files but only ${room} more can be added. Select fewer files and try again.`
        : "No more files can be added here. Remove one first.";
    const add = async (picked) => {
        const room = Math.max(0, maxFiles - documents.value.length);
        const files = picked.slice(0, room);
        notice.value = files.length < picked.length ? capMessage(picked.length, room) : null;
        if (!files.length)
            return;
        const start = documents.value.length;
        documents.value.push(...files.map((file) => ({
            file,
            link: null,
            uploading: true,
            error: null,
        })));
        await Promise.all(documents.value.slice(start).map(uploadOne));
    };
    const remove = (doc) => {
        documents.value = documents.value.filter((staged) => staged !== doc);
    };
    const reset = () => {
        documents.value = [];
        notice.value = null;
    };
    return { documents, links, uploading, notice, add, remove, reset };
}
